import { useCallback, useEffect, useState } from 'react';
import { ErroApi } from '../lib/api';
import { useAutenticacao } from '../lib/auth';
import { buscarMinisterios, type Ministerio } from '../lib/escalas';
import {
  buscarLancamentos,
  buscarResumo,
  criarLancamento,
  estornarLancamento,
  fecharMes,
  paraCentavos,
  reais,
  salvarOrcamento,
  CATEGORIAS_ENTRADA,
  CATEGORIAS_SAIDA,
  FORMAS,
  NOME_CATEGORIA,
  NOME_FORMA,
  urlInformeAnual,
  urlRecibo,
  type Lancamento,
  type Resumo,
} from '../lib/financeiro';
import { useConfirmacao } from './Confirmacao';
import { Alerta, Botao, Cartao, LinkBotao } from './Interface';

const MESES = [
  'Janeiro',
  'Fevereiro',
  'Marco',
  'Abril',
  'Maio',
  'Junho',
  'Julho',
  'Agosto',
  'Setembro',
  'Outubro',
  'Novembro',
  'Dezembro',
];

function hojeSP(): string {
  return new Intl.DateTimeFormat('en-CA', { timeZone: 'America/Sao_Paulo' }).format(new Date());
}

export default function FinanceiroMes({ ano, mes }: { ano: number; mes: number }) {
  const { usuario } = useAutenticacao();
  const podeLancar = usuario?.papel === 'admin';

  const [resumo, setResumo] = useState<Resumo | null>(null);
  const [lancamentos, setLancamentos] = useState<Lancamento[]>([]);
  const [ministerios, setMinisterios] = useState<Ministerio[]>([]);
  const [fechado, setFechado] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const [aviso, setAviso] = useState<string | null>(null);
  const [ocupado, setOcupado] = useState(false);

  // formulario de lancamento
  const [tipo, setTipo] = useState<'entrada' | 'saida'>('entrada');
  const [categoria, setCategoria] = useState('oferta');
  const [valor, setValor] = useState('');
  const [dia, setDia] = useState(hojeSP());
  const [descricao, setDescricao] = useState('');
  const [ministerioId, setMinisterioId] = useState('');
  const [forma, setForma] = useState('pix');

  const [membroDoInforme, setMembroDoInforme] = useState('');

  const { confirmar, dialogo } = useConfirmacao();

  const carregar = useCallback(async () => {
    try {
      const [r, l, m] = await Promise.all([
        buscarResumo(ano, mes),
        buscarLancamentos(ano, mes),
        buscarMinisterios(),
      ]);
      setResumo(r);
      setLancamentos(l.lancamentos);
      setFechado(l.fechado);
      setMinisterios(m.ministerios);
      setErro(null);
    } catch (e) {
      setErro(e instanceof ErroApi ? e.message : 'Não consegui carregar a tesouraria.');
    }
  }, [ano, mes]);

  useEffect(() => {
    void carregar();
  }, [carregar]);

  function trocarTipo(novo: 'entrada' | 'saida') {
    setTipo(novo);
    setCategoria(novo === 'entrada' ? 'oferta' : 'material');
    if (novo === 'entrada') setMinisterioId('');
  }

  async function lancar(confirmarMesmoAssim = false) {
    const centavos = paraCentavos(valor);
    if (centavos === null || centavos <= 0) {
      setErro('Informe o valor, por exemplo 1.234,56.');
      return;
    }
    if (!descricao.trim()) {
      setErro('Diga do que se trata.');
      return;
    }
    setOcupado(true);
    setErro(null);
    try {
      await criarLancamento({
        tipo,
        categoria,
        valorCentavos: centavos,
        dia,
        descricao: descricao.trim(),
        ministerioId: ministerioId ? Number(ministerioId) : null,
        forma,
        ...(confirmarMesmoAssim ? { confirmarMesmoAssim: true } : {}),
      });
      setValor('');
      setDescricao('');
      setAviso('Lancamento registrado.');
      await carregar();
    } catch (e) {
      // estouro de teto que apenas avisa: o tesoureiro decide na hora
      if (e instanceof ErroApi && e.codigo === 'orcamento_estourado') {
        const ok = await confirmar({
          titulo: 'Passa do orçamento',
          texto: `${e.message} Lancar assim mesmo?`,
          confirmar: 'Lancar mesmo assim',
        });
        setOcupado(false);
        if (ok) await lancar(true);
        return;
      }
      setErro(e instanceof ErroApi ? e.message : 'Não consegui lancar.');
    } finally {
      setOcupado(false);
    }
  }

  async function estornar(l: Lancamento) {
    const ok = await confirmar({
      titulo: 'Estornar este lancamento?',
      texto: `Entra uma linha nova de ${reais(-l.valorCentavos)} anulando "${l.descricao}". As duas continuam visiveis — lancamento nao se apaga.`,
      confirmar: 'Estornar',
    });
    if (!ok) return;
    try {
      await estornarLancamento(l.id, `Corrigido em ${hojeSP()}`);
      setAviso('Estorno registrado.');
      await carregar();
    } catch (e) {
      setErro(e instanceof ErroApi ? e.message : 'Não consegui estornar.');
    }
  }

  const categorias = tipo === 'entrada' ? CATEGORIAS_ENTRADA : CATEGORIAS_SAIDA;

  // quem aparece como doador no mes; a chave e o id (ARMADILHA 10). Sai dos
  // lancamentos ja carregados, entao nao precisa de rota nova.
  const membrosComContribuicao = [
    ...new Map(
      lancamentos
        .filter((l) => l.membroId && l.membroNome)
        .map((l) => [l.membroId!, { id: l.membroId!, nome: l.membroNome! }]),
    ).values(),
  ].sort((a, b) => a.nome.localeCompare(b.nome));

  return (
    <>
      {dialogo}

      {erro && (
        <div className="mb-4 max-w-2xl">
          <Alerta>{erro}</Alerta>
        </div>
      )}
      {aviso && (
        <div className="mb-4 max-w-2xl">
          <Alerta tipo="ok">{aviso}</Alerta>
        </div>
      )}
      {fechado && (
        <div className="mb-4 max-w-2xl">
          <Alerta tipo="aviso">
            {MESES[mes - 1]} de {ano} esta fechado. Nada entra nem sai deste mes.
          </Alerta>
        </div>
      )}

      <div className="grid gap-4 lg:grid-cols-3">
        <Cartao>
          <p className="text-sm text-tinta-400">Entrou</p>
          <p className="mt-1 text-2xl font-semibold tabular-nums text-tinta-900">
            {reais(resumo?.entradasCentavos ?? 0)}
          </p>
        </Cartao>
        <Cartao>
          <p className="text-sm text-tinta-400">Saiu</p>
          <p className="mt-1 text-2xl font-semibold tabular-nums text-tinta-900">
            {reais(resumo?.saidasCentavos ?? 0)}
          </p>
        </Cartao>
        <Cartao>
          <p className="text-sm text-tinta-400">Saldo do mês</p>
          <p
            className={`mt-1 text-2xl font-semibold tabular-nums ${
              (resumo?.saldoCentavos ?? 0) < 0 ? 'text-red-700' : 'text-tinta-900'
            }`}
          >
            {reais(resumo?.saldoCentavos ?? 0)}
          </p>
        </Cartao>
      </div>

      <div className="mt-4 grid gap-4 lg:grid-cols-[2fr_1fr]">
        <div className="space-y-4">
          {podeLancar && !fechado && (
            <Cartao>
              <h2 className="font-semibold text-tinta-900">Novo lancamento</h2>
              <div className="mt-3 flex gap-2">
                {(['entrada', 'saida'] as const).map((t) => (
                  <button
                    key={t}
                    onClick={() => trocarTipo(t)}
                    className={`rounded-xl px-3 py-1.5 text-sm font-medium ${
                      tipo === t ? 'bg-tinta-900 text-white' : 'bg-tinta-100 text-tinta-600'
                    }`}
                  >
                    {t === 'entrada' ? 'Entrada' : 'Saida'}
                  </button>
                ))}
              </div>

              <div className="mt-3 grid gap-2 sm:grid-cols-2">
                <label className="text-sm">
                  <span className="mb-1 block text-tinta-600">Valor</span>
                  <input
                    value={valor}
                    inputMode="decimal"
                    onChange={(e) => setValor(e.target.value)}
                    placeholder="1.234,56"
                    className="w-full rounded-xl border border-tinta-200 px-3 py-2 text-sm tabular-nums"
                  />
                </label>
                <label className="text-sm">
                  <span className="mb-1 block text-tinta-600">Dia</span>
                  <input
                    type="date"
                    value={dia}
                    onChange={(e) => setDia(e.target.value)}
                    className="w-full rounded-xl border border-tinta-200 px-3 py-2 text-sm"
                  />
                </label>
                <label className="text-sm">
                  <span className="mb-1 block text-tinta-600">Categoria</span>
                  <select
                    value={categoria}
                    onChange={(e) => setCategoria(e.target.value)}
                    className="w-full rounded-xl border border-tinta-200 bg-white px-3 py-2 text-sm"
                  >
                    {categorias.map((c) => (
                      <option key={c} value={c}>
                        {NOME_CATEGORIA[c] ?? c}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="text-sm">
                  <span className="mb-1 block text-tinta-600">Forma</span>
                  <select
                    value={forma}
                    onChange={(e) => setForma(e.target.value)}
                    className="w-full rounded-xl border border-tinta-200 bg-white px-3 py-2 text-sm"
                  >
                    {FORMAS.map((f) => (
                      <option key={f} value={f}>
                        {NOME_FORMA[f] ?? f}
                      </option>
                    ))}
                  </select>
                </label>
                {tipo === 'saida' && (
                  <label className="text-sm sm:col-span-2">
                    <span className="mb-1 block text-tinta-600">Ministério (centro de custo)</span>
                    <select
                      value={ministerioId}
                      onChange={(e) => setMinisterioId(e.target.value)}
                      className="w-full rounded-xl border border-tinta-200 bg-white px-3 py-2 text-sm"
                    >
                      <option value="">Sem ministério</option>
                      {ministerios.map((m) => (
                        <option key={m.id} value={m.id}>
                          {m.nome}
                        </option>
                      ))}
                    </select>
                  </label>
                )}
                <label className="text-sm sm:col-span-2">
                  <span className="mb-1 block text-tinta-600">Do que se trata</span>
                  <input
                    value={descricao}
                    maxLength={255}
                    onChange={(e) => setDescricao(e.target.value)}
                    placeholder="Oferta do culto de domingo"
                    className="w-full rounded-xl border border-tinta-200 px-3 py-2 text-sm"
                  />
                </label>
              </div>

              <div className="mt-3">
                <Botao carregando={ocupado} onClick={() => void lancar()}>
                  Lancar
                </Botao>
              </div>
            </Cartao>
          )}

          <Cartao>
            <div className="mb-3 flex items-center justify-between">
              <h2 className="font-semibold text-tinta-900">
                Movimento de {MESES[mes - 1]}{' '}
                <span className="font-normal text-tinta-400">({lancamentos.length})</span>
              </h2>
            </div>

            {lancamentos.length === 0 ? (
              <p className="text-sm text-tinta-400">Nenhum lancamento neste mês.</p>
            ) : (
              <ul className="space-y-1">
                {lancamentos.map((l) => {
                  const anulado = !!l.estornadoPor;
                  const ehEstorno = !!l.estornaId;
                  return (
                    <li
                      key={l.id}
                      className={`flex flex-wrap items-center gap-x-3 gap-y-1 rounded-xl px-3 py-2 text-sm ${
                        ehEstorno ? 'bg-tinta-100' : 'bg-tinta-100'
                      } ${anulado ? 'opacity-60' : ''}`}
                    >
                      <span className="w-14 shrink-0 text-xs tabular-nums text-tinta-400">
                        {l.dia.slice(8, 10)}/{l.dia.slice(5, 7)}
                      </span>
                      <span
                        className={`shrink-0 tabular-nums font-semibold ${
                          l.valorCentavos < 0 ? 'text-tinta-400' : 'text-tinta-900'
                        }`}
                      >
                        {l.tipo === 'saida' && l.valorCentavos > 0 ? '-' : ''}
                        {reais(Math.abs(l.valorCentavos))}
                      </span>
                      <span className={`min-w-0 flex-1 truncate ${anulado ? 'line-through' : ''}`}>
                        {l.descricao}
                      </span>
                      <span className="shrink-0 rounded-md bg-white px-2 py-0.5 text-xs text-tinta-600">
                        {NOME_CATEGORIA[l.categoria] ?? l.categoria}
                      </span>
                      {l.ministerioNome && (
                        <span className="shrink-0 text-xs text-tinta-400">{l.ministerioNome}</span>
                      )}
                      {anulado && (
                        <span className="shrink-0 rounded-md bg-tinta-200 px-2 py-0.5 text-xs text-tinta-600">
                          estornado
                        </span>
                      )}
                      {podeLancar && !anulado && !ehEstorno && !fechado && (
                        <button
                          onClick={() => void estornar(l)}
                          className="shrink-0 text-xs text-tinta-400 hover:text-red-600"
                        >
                          estornar
                        </button>
                      )}
                      {/* recibo so no que nao e estorno; entrada anonima o
                          servidor recusa, entao o link nem aparece */}
                      {!ehEstorno && (l.tipo === 'saida' || l.membroId) && (
                        <a
                          href={urlRecibo(l.id)}
                          target="_blank"
                          rel="noreferrer"
                          className="shrink-0 text-xs text-tinta-400 hover:text-tinta-700"
                        >
                          recibo
                        </a>
                      )}
                    </li>
                  );
                })}
              </ul>
            )}
          </Cartao>
        </div>

        <div className="space-y-4">
          {(resumo?.orcamentos.length ?? 0) > 0 && (
            <Cartao>
              <h2 className="font-semibold text-tinta-900">Orçamento do mês</h2>
              <ul className="mt-3 space-y-3">
                {resumo!.orcamentos.map((o) => {
                  const parte = o.tetoCentavos
                    ? Math.min((o.gastoCentavos / o.tetoCentavos) * 100, 100)
                    : 0;
                  const estourou = o.gastoCentavos > o.tetoCentavos;
                  return (
                    <li key={o.ministerioId}>
                      <div className="flex items-baseline justify-between gap-2 text-sm">
                        <span className="font-medium text-tinta-800">{o.ministerioNome}</span>
                        <span
                          className={`tabular-nums text-xs ${
                            estourou ? 'font-semibold text-red-700' : 'text-tinta-400'
                          }`}
                        >
                          {reais(o.gastoCentavos)} / {reais(o.tetoCentavos)}
                        </span>
                      </div>
                      <div className="mt-1 h-1.5 overflow-hidden rounded-full bg-tinta-200">
                        <div
                          className={`h-full rounded-full ${estourou ? 'bg-red-600' : 'bg-tinta-900'}`}
                          style={{ width: `${parte}%` }}
                        />
                      </div>
                    </li>
                  );
                })}
              </ul>
            </Cartao>
          )}

          {(resumo?.entradasPorCategoria.length ?? 0) > 0 && (
            <Cartao>
              <h2 className="font-semibold text-tinta-900">Entrou por tipo</h2>
              <ul className="mt-2 space-y-1 text-sm">
                {resumo!.entradasPorCategoria.map((c) => (
                  <li key={c.categoria} className="flex justify-between gap-2">
                    <span className="text-tinta-600">{NOME_CATEGORIA[c.categoria] ?? c.categoria}</span>
                    <span className="tabular-nums text-tinta-900">{reais(c.centavos)}</span>
                  </li>
                ))}
              </ul>
            </Cartao>
          )}

          {(resumo?.saidasPorMinisterio.length ?? 0) > 0 && (
            <Cartao>
              <h2 className="font-semibold text-tinta-900">Saiu por ministério</h2>
              <ul className="mt-2 space-y-1 text-sm">
                {resumo!.saidasPorMinisterio.map((m) => (
                  <li key={m.id ?? 'sem'} className="flex justify-between gap-2">
                    <span className="text-tinta-600">{m.nome}</span>
                    <span className="tabular-nums text-tinta-900">{reais(m.centavos)}</span>
                  </li>
                ))}
              </ul>
            </Cartao>
          )}

          {podeLancar && (
            <Cartao>
              <h2 className="font-semibold text-tinta-900">Teto por ministério</h2>
              <p className="mt-1 text-xs text-tinta-400">
                Define quanto cada ministerio pode gastar em {MESES[mes - 1]}.
              </p>
              <DefinirTeto
                ministerios={ministerios}
                ano={ano}
                mes={mes}
                aoSalvar={async (texto) => {
                  setAviso(texto);
                  await carregar();
                }}
                aoErrar={setErro}
              />
            </Cartao>
          )}

          {podeLancar && !fechado && (
            <Cartao>
              <h2 className="font-semibold text-tinta-900">Fechar o mês</h2>
              <p className="mt-1 text-xs text-tinta-400">
                Depois de apresentar a prestacao de contas, feche o mes: ninguem mais lanca nem
                estorna dentro dele.
              </p>
              <Botao
                variante="secundario"
                className="mt-3 w-full text-sm"
                onClick={async () => {
                  const ok = await confirmar({
                    titulo: `Fechar ${MESES[mes - 1]} de ${ano}?`,
                    texto: 'Nenhum lancamento novo entra neste mês depois disso. Da para reabrir, e a reabertura fica no log.',
                    confirmar: 'Fechar o mês',
                  });
                  if (!ok) return;
                  try {
                    await fecharMes(ano, mes);
                    setAviso(`${MESES[mes - 1]} fechado.`);
                    await carregar();
                  } catch (e) {
                    setErro(e instanceof ErroApi ? e.message : 'Não consegui fechar o mês.');
                  }
                }}
              >
                Fechar {MESES[mes - 1]}
              </Botao>
            </Cartao>
          )}

          <Cartao>
            <h2 className="font-semibold text-tinta-900">Informe anual</h2>
            <p className="mt-1 text-xs text-tinta-400">
              O documento que o membro pede em janeiro. Ele tambem baixa o proprio, sozinho.
            </p>
            <div className="mt-3 space-y-2">
              <select
                value={membroDoInforme}
                onChange={(e) => setMembroDoInforme(e.target.value)}
                className="w-full rounded-xl border border-tinta-200 bg-white px-3 py-2 text-sm"
              >
                <option value="">Escolha a pessoa</option>
                {membrosComContribuicao.map((m) => (
                  <option key={m.id} value={m.id}>
                    {m.nome}
                  </option>
                ))}
              </select>
              {membroDoInforme && (
                <LinkBotao
                  href={urlInformeAnual(Number(membroDoInforme), ano)}
                  target="_blank"
                  rel="noreferrer"
                  className="w-full"
                >
                  Baixar informe de {ano}
                </LinkBotao>
              )}
            </div>
          </Cartao>
        </div>
      </div>
    </>
  );
}

function DefinirTeto({
  ministerios,
  ano,
  mes,
  aoSalvar,
  aoErrar,
}: {
  ministerios: Ministerio[];
  ano: number;
  mes: number;
  aoSalvar: (texto: string) => Promise<void>;
  aoErrar: (texto: string) => void;
}) {
  const [ministerioId, setMinisterioId] = useState('');
  const [teto, setTeto] = useState('');
  const [acao, setAcao] = useState('avisar');
  const [salvando, setSalvando] = useState(false);

  return (
    <div className="mt-3 space-y-2">
      <select
        value={ministerioId}
        onChange={(e) => setMinisterioId(e.target.value)}
        className="w-full rounded-xl border border-tinta-200 bg-white px-3 py-2 text-sm"
      >
        <option value="">Escolha o ministério</option>
        {ministerios.map((m) => (
          <option key={m.id} value={m.id}>
            {m.nome}
          </option>
        ))}
      </select>
      <div className="flex gap-2">
        <input
          value={teto}
          inputMode="decimal"
          onChange={(e) => setTeto(e.target.value)}
          placeholder="Teto (1.000,00)"
          className="min-w-0 flex-1 rounded-xl border border-tinta-200 px-3 py-2 text-sm tabular-nums"
        />
        <select
          value={acao}
          onChange={(e) => setAcao(e.target.value)}
          className="rounded-xl border border-tinta-200 bg-white px-3 py-2 text-sm"
        >
          <option value="avisar">Avisar</option>
          <option value="bloquear">Bloquear</option>
        </select>
      </div>
      <Botao
        variante="secundario"
        className="w-full text-sm"
        carregando={salvando}
        onClick={async () => {
          const centavos = paraCentavos(teto);
          if (!ministerioId || centavos === null) {
            aoErrar('Escolha o ministério e informe o teto.');
            return;
          }
          setSalvando(true);
          try {
            await salvarOrcamento({
              ministerioId: Number(ministerioId),
              ano,
              mes,
              tetoCentavos: centavos,
              acao,
            });
            setTeto('');
            setMinisterioId('');
            await aoSalvar('Teto salvo.');
          } catch (e) {
            aoErrar(e instanceof ErroApi ? e.message : 'Não consegui salvar o teto.');
          } finally {
            setSalvando(false);
          }
        }}
      >
        Salvar teto
      </Botao>
    </div>
  );
}
